'use client';

import { AlertModal } from '@/components/shared/modals';
import {
  BarChart,
  Upload,
  MessageSquare,
  Building2,
  CalendarClock,
  Webhook,
  Radar,
  Repeat,
  type LucideIcon,
} from 'lucide-react';
import { useSixsenseAudienceAuthStatus } from '@/hooks/useSixsenseAudience';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { PATH } from '@/constants';
import { AUDIENCE_CREATION_METHOD } from '@/constants/audiences';
import { useRouter } from 'next/navigation';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AUDIENCE_CREATION_STARTED } from '@/lib/analytics-events';

/**
 * Menu entries that aren't a creation method of their own. Non-responders build a list the user
 * saves as an ordinary CSV audience, so there is no 'non_responders' audience type.
 */
const NON_RESPONDERS_OPTION = 'non_responders' as const;
/**
 * Also not an audience type. It creates an ordinary `salesforce` person audience whose selector
 * is an uploaded company list rather than filter rules (mb-server#6278).
 */
const SALESFORCE_COMPANIES_OPTION = 'salesforce_companies' as const;
type AudienceCreationOptionId = AUDIENCE_CREATION_METHOD
  | typeof NON_RESPONDERS_OPTION
  | typeof SALESFORCE_COMPANIES_OPTION;

interface AudienceCreationOption {
  id: AudienceCreationOptionId;
  label: string;
  description: string;
  icon: LucideIcon | string;
}

interface AddAudienceModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onOpenCsvModal: () => void;
  onOpenWebsiteVisitModal?: () => void;
  onOpenHubSpotModal?: () => void;
  onOpenSalesforceModal?: () => void;
  onOpenLinkedInPostCommentersForm?: () => void;
  onOpenTamBuilderForm?: () => void;
  onOpenScheduledImportForm?: () => void;
  onOpenWebhookForm?: () => void;
  onOpen6senseModal?: () => void;
  onOpenNonRespondersModal?: () => void;
  onOpenSalesforceCompaniesModal?: () => void;
}

const AUDIENCE_CREATION_OPTIONS: AudienceCreationOption[] = [
  {
    id: AUDIENCE_CREATION_METHOD.RULES,
    label: 'Website visits',
    description: 'Build segments from visitor behavior on your site',
    icon: BarChart,
  },
  {
    id: AUDIENCE_CREATION_METHOD.CSV,
    label: 'Upload file',
    description: 'Import contacts from a CSV or Excel file',
    icon: Upload,
  },
  {
    id: AUDIENCE_CREATION_METHOD.HUBSPOT,
    label: 'HubSpot contacts',
    description: 'Pull contacts from your connected HubSpot account',
    icon: '/images/brand/hubspotMB.svg',
  },
  {
    id: AUDIENCE_CREATION_METHOD.SALESFORCE,
    label: 'Salesforce records',
    description: 'Import leads or contacts from Salesforce',
    icon: '/images/brand/salesforce.svg',
  },
  {
    id: SALESFORCE_COMPANIES_OPTION,
    label: 'Companies from Salesforce',
    description: 'Upload a list of companies and import the contacts and leads you already have at them in Salesforce',
    icon: Building2,
  },
  {
    id: NON_RESPONDERS_OPTION,
    label: 'Non-responders from another audience',
    description: 'People an audience emailed who never replied: valid emails only, no bounces or unsubscribes',
    icon: Repeat,
  },
  {
    id: AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS,
    label: 'LinkedIn post commenters',
    description: 'Capture everyone who commented on a LinkedIn post',
    icon: MessageSquare,
  },
  {
    id: AUDIENCE_CREATION_METHOD.FIBER_TAM,
    label: 'TAM Builder',
    description: 'Find companies that match your ideal customer profile',
    icon: Building2,
  },
  {
    id: AUDIENCE_CREATION_METHOD.SCHEDULED_IMPORT,
    label: 'Scheduled import',
    description: 'Re-sync from HubSpot or Salesforce on a recurring schedule',
    icon: CalendarClock,
  },
  {
    id: AUDIENCE_CREATION_METHOD.WEBHOOK,
    label: 'Webhook',
    description: 'Receive contacts via a webhook — fields are mapped by AI automatically',
    icon: Webhook,
  },
  {
    // Shown only when the workspace has an active 6sense integration (gated below).
    id: AUDIENCE_CREATION_METHOD.SIXSENSE,
    label: '6sense accounts',
    description: 'Build an account audience from your 6sense intent, firmographic, and segment data',
    icon: Radar,
  },
];

// Hidden from the menu for now (2026-10-06, revisit 2026-11-05). Scheduled import is
// moving into Loop, and TAM Builder isn't needed yet. Existing audiences of these types
// keep working and syncing; only creating new ones from this menu is hidden. To bring
// one back, remove it from this set.
const HIDDEN_CREATION_METHODS = new Set<AudienceCreationOptionId>([
  AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS,
  AUDIENCE_CREATION_METHOD.FIBER_TAM,
  AUDIENCE_CREATION_METHOD.SCHEDULED_IMPORT,
]);

export const AddAudienceModal: React.FC<AddAudienceModalProps> = ({
  open,
  onOpenChange,
  onOpenCsvModal,
  onOpenWebsiteVisitModal,
  onOpenHubSpotModal,
  onOpenSalesforceModal,
  onOpenLinkedInPostCommentersForm,
  onOpenTamBuilderForm,
  onOpenScheduledImportForm,
  onOpenWebhookForm,
  onOpen6senseModal,
  onOpenNonRespondersModal,
  onOpenSalesforceCompaniesModal,
}) => {
  const router = useRouter();
  const { capture } = useAnalytics();

  const modalOpen = open ?? false;
  const handleOpenChange = onOpenChange ?? (() => {});

  // Gate the 6sense source on an active integration (server-enforced too). Only probe
  // while the modal is open to avoid an eager call on every audiences-page load.
  const { data: sixsenseStatus } = useSixsenseAudienceAuthStatus({ enabled: modalOpen });
  const visibleOptions = AUDIENCE_CREATION_OPTIONS.filter(
    (o) =>
      !HIDDEN_CREATION_METHODS.has(o.id) &&
      (o.id !== AUDIENCE_CREATION_METHOD.SIXSENSE || sixsenseStatus?.connected),
  );

  const handleSelectOption = (option: AudienceCreationOption) => {
    capture(AUDIENCE_CREATION_STARTED, { creation_method: option.id });
    handleOpenChange(false);

    // Dispatch map — avoids an ever-growing if-else chain.
    const handlers: Partial<Record<AudienceCreationOptionId, (() => void) | undefined>> = {
      [AUDIENCE_CREATION_METHOD.CSV]: onOpenCsvModal,
      [AUDIENCE_CREATION_METHOD.RULES]: onOpenWebsiteVisitModal,
      [AUDIENCE_CREATION_METHOD.HUBSPOT]: onOpenHubSpotModal,
      [AUDIENCE_CREATION_METHOD.SALESFORCE]: onOpenSalesforceModal,
      [AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS]: onOpenLinkedInPostCommentersForm,
      [AUDIENCE_CREATION_METHOD.FIBER_TAM]: onOpenTamBuilderForm,
      [AUDIENCE_CREATION_METHOD.SCHEDULED_IMPORT]: onOpenScheduledImportForm,
      [AUDIENCE_CREATION_METHOD.WEBHOOK]: onOpenWebhookForm,
      [AUDIENCE_CREATION_METHOD.SIXSENSE]: onOpen6senseModal,
      [NON_RESPONDERS_OPTION]: onOpenNonRespondersModal,
      [SALESFORCE_COMPANIES_OPTION]: onOpenSalesforceCompaniesModal,
    };

    const handler = handlers[option.id];
    if (handler) { handler(); return; }

    router.push(PATH.AUDIENCES);
  };

  return (
    <AlertModal open={modalOpen} onOpenChange={handleOpenChange} title="Add audience" size="lg">
      <div className="space-y-2">
        {visibleOptions.map((option) => {
          const IconComponent = typeof option.icon !== 'string' ? option.icon : null;

          return (
            <button
              key={option.id}
              type="button"
              className={cn(
                'flex w-full items-center gap-4 p-4 rounded-lg cursor-pointer transition-all text-left',
                'border border-slate-200 hover:border-slate-300 hover:bg-slate-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mb-blue-500 focus-visible:ring-offset-1',
              )}
              onClick={() => handleSelectOption(option)}
            >
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100">
                {IconComponent ? (
                  <IconComponent className="w-5 h-5 text-slate-600" />
                ) : (
                  <Image src={option.icon as string} alt={option.label} width={20} height={20} />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-slate-900">{option.label}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{option.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </AlertModal>
  );
};
